import { z } from 'zod';

export const feedbackSchema = z.object({
  id: z.string(),
  fragranceId: z.string(),
  createdAt: z.string(),
  enjoyedSmell: z.boolean(),
  feltLikeMe: z.boolean(),
  wouldWear: z.boolean(),
  descriptors: z.array(
    z.enum([
      'too_subtle',
      'too_noticeable',
      'too_sweet',
      'too_fresh',
      'too_warm',
      'too_dry',
      'too_floral',
      'too_woody',
      'too_heavy',
      'too_clean',
      'too_familiar',
      'too_unusual',
    ]),
  ),
  longevity: z.enum(['too_short', 'suitable', 'too_long', 'not_sure']),
  testedOn: z.enum(['paper', 'skin']),
  immediateReaction: z.enum(['love', 'like', 'unsure', 'dislike']),
  laterReaction: z.enum(['improved', 'stable', 'faded', 'worsened', 'not_checked']),
  comment: z.string().max(800),
});

export type TestingFeedback = z.infer<typeof feedbackSchema>;

const descriptorEffects: Record<string, [string, number]> = {
  too_sweet: ['sweet', -1],
  too_fresh: ['fresh', -1],
  too_warm: ['amber', -1],
  too_dry: ['dry', -1],
  too_floral: ['floral', -1],
  too_woody: ['woody', -1],
  too_heavy: ['heavy', -1],
  too_clean: ['clean', -1],
  too_familiar: ['unconventional', 0.65],
  too_unusual: ['unconventional', -1],
  too_subtle: ['intensity', 0.75],
  too_noticeable: ['intensity', -0.75],
};

export interface PreferenceUpdate {
  weights: Record<string, number>;
  changes: string[];
}

const changeCopy: Record<string, [string, string]> = {
  sweet: ['increased tolerance for sweetness', 'reduced preference for sweetness'],
  fresh: ['increased preference for fresh profiles', 'reduced preference for fresh profiles'],
  amber: ['increased preference for warm amber profiles', 'reduced preference for warm profiles'],
  dry: ['increased preference for dry structures', 'reduced preference for dry structures'],
  floral: ['increased preference for florals', 'reduced preference for floral profiles'],
  woody: ['increased preference for woods', 'reduced preference for woody profiles'],
  heavy: ['increased tolerance for weight', 'reduced preference for heavy profiles'],
  clean: ['increased preference for clean effects', 'reduced preference for clean effects'],
  unconventional: ['increased appetite for unusual profiles', 'reduced appetite for unusual profiles'],
  intensity: ['increased desired intensity', 'reduced desired intensity'],
};

export const updatePreferenceWeights = (
  current: Record<string, number>,
  feedback: TestingFeedback,
): PreferenceUpdate => {
  const weights = { ...current };
  const deltas = new Map<string, number>();
  for (const descriptor of feedback.descriptors) {
    const effect = descriptorEffects[descriptor];
    if (effect) deltas.set(effect[0], (deltas.get(effect[0]) ?? 0) + effect[1]);
  }
  if (feedback.enjoyedSmell && feedback.wouldWear) {
    deltas.set('tested_positive', 1);
  }
  if (!feedback.feltLikeMe) deltas.set('identity_correction', 1);
  if (feedback.longevity === 'too_short') deltas.set('intensity', (deltas.get('intensity') ?? 0) + 0.5);
  if (feedback.longevity === 'too_long') deltas.set('intensity', (deltas.get('intensity') ?? 0) - 0.5);

  const changes: string[] = [];
  for (const [key, delta] of deltas) {
    weights[key] = Math.max(-3, Math.min(3, (weights[key] ?? 0) + delta));
    const copy = changeCopy[key];
    if (copy && delta !== 0) changes.push(delta > 0 ? copy[0] : copy[1]);
  }
  if (feedback.enjoyedSmell && feedback.wouldWear) changes.push('strengthened signals from this fragrance');
  if (!feedback.feltLikeMe) changes.push('opened a correction away from this exact identity match');
  return { weights, changes: [...new Set(changes)] };
};
